import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { reportLabels } from '../constants/reportLabels';
import { Server } from '../services/Server';
import type { ChartSubmission, DateValue, GeneratedReport, Report, ReportExportProgress } from '../types';
import { handleError } from '../utils/Error';
import { captureReportCharts } from '../utils/chartCapture';
import { readReportProblem, reportErrorMessage } from '../utils/reportErrors';
import { fitChartsToLimits, planReportCharts } from '../utils/reportCharts';
import { triggerFileDownload } from '../utils/reports';

const CONTEXT_MAX_AGE_MS = 5_000;
const REPORTS_PAGE_SIZE = 50;
const WARNINGS_TOAST_MS = 12_000;

const reportContextKey = (projectId: string) => ['report-context', projectId];
const reportsKey = (projectId: string) => ['reports', projectId];

type ServerApi = ReturnType<typeof Server>;

/** Fields the saved project carries that the shared Project type does not list. */
interface SavedProjectFields {
  results?: { solution?: unknown };
  yearly_cleaning_days?: DateValue[];
  rain_threshold?: number;
}

interface PreparedCharts {
  charts: ChartSubmission[];
  warnings: string[];
}

const isAbortError = (error: unknown) => (error instanceof DOMException && error.name === 'AbortError') || axios.isCancel(error);

const notifyReportError = async (error: unknown, fallback?: string) => {
  const problem = await readReportProblem(error);
  if (problem.status === 401) {
    handleError(error);
    return;
  }
  const message = reportErrorMessage(problem, fallback);
  toast.error(message, { id: `report-error:${message}` });
};

const notifyReportCreated = (report: GeneratedReport, warnings: string[]) => {
  if (!warnings.length) {
    toast.success(reportLabels.created(report.version));
    return;
  }
  toast.success(reportLabels.created_with_warnings(report.version, warnings), {
    duration: WARNINGS_TOAST_MS,
    style: { whiteSpace: 'pre-line', maxWidth: 560 },
  });
};

/** Draws the charts of the up-to-date packages from their stored results and the saved project inputs. */
const prepareReportCharts = async (
  projectId: string,
  api: ServerApi,
  queryClient: QueryClient,
  signal: AbortSignal,
  onProgress: (progress: ReportExportProgress) => void,
): Promise<PreparedCharts> => {
  const [context, project, cleaningPackages] = await Promise.all([
    api.getReportContextAPI(projectId, { includeResults: true }, signal),
    api.getProjectAPI(projectId),
    queryClient.ensureQueryData({ queryKey: ['cleaning_packages'], queryFn: () => api.getCleaningPackagesAPI() }).catch(() => ({})),
  ]);
  const savedProject = project as (typeof project & SavedProjectFields) | undefined;
  const savedSolution = savedProject?.results?.solution;
  const plan = planReportCharts(context, {
    savedSolution: typeof savedSolution === 'string' ? savedSolution : undefined,
    yearlyCleaningDays: savedProject?.yearly_cleaning_days,
    rainThreshold: savedProject?.rain_threshold,
    cleaningPackages: cleaningPackages ?? {},
  });
  const captured = await captureReportCharts(plan.jobs, {
    signal,
    onProgress: (done, total) => onProgress({ phase: 'rendering', done, total }),
  });
  const fitted = fitChartsToLimits(captured.charts);
  return {
    charts: fitted.kept.map((chart) => chart.submission),
    warnings: [...plan.warnings, ...captured.warnings, ...fitted.warnings],
  };
};

export const useReportContext = (projectId: string) => {
  const { getReportContextAPI } = Server();
  const { data, isError, isFetching, dataUpdatedAt, refetch } = useQuery({
    queryKey: reportContextKey(projectId),
    queryFn: ({ signal }) => getReportContextAPI(projectId, { includeResults: false }, signal),
  });

  // Analyses store their results server-side without saving the project, so the context is re-checked on demand.
  const refreshIfOld = useCallback(() => {
    if (!isFetching && Date.now() - dataUpdatedAt > CONTEXT_MAX_AGE_MS) {
      refetch();
    }
  }, [isFetching, dataUpdatedAt, refetch]);

  return { context: data, isError, refreshIfOld };
};

/** Reports of the project, most recent first, loaded page by page. */
export const useReports = (projectId: string, enabled: boolean) => {
  const { getReportsAPI } = Server();
  const query = useInfiniteQuery({
    queryKey: reportsKey(projectId),
    queryFn: ({ pageParam }) => getReportsAPI(projectId, { limit: REPORTS_PAGE_SIZE, skip: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((count, page) => count + page.items.length, 0);
      return lastPage.items.length > 0 && loaded < lastPage.total ? loaded : undefined;
    },
    enabled,
  });
  const pages = query.data?.pages;
  // A report created between two pages shifts the next one: the same report may come twice.
  const reports = useMemo(() => {
    const seen = new Set<string>();
    return (pages ?? []).flatMap((page) => page.items).filter((report) => !seen.has(report.id) && !!seen.add(report.id));
  }, [pages]);
  const total = pages?.length ? Math.max(pages[pages.length - 1].total, reports.length) : 0;

  return { ...query, reports, total };
};

export const useCreateReport = (projectId: string) => {
  const queryClient = useQueryClient();
  const api = Server();
  const [progress, setProgress] = useState<ReportExportProgress | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => abortRef.current?.abort(), []);
  useEffect(() => cancel, [cancel]);

  const mutation = useMutation({
    mutationFn: async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      setProgress({ phase: 'preparing', done: 0, total: 0 });
      // The charts only illustrate the report: failing to draw them never prevents generating it.
      const prepared = await prepareReportCharts(projectId, api, queryClient, controller.signal, setProgress).catch((error) => {
        if (isAbortError(error) || controller.signal.aborted) throw error;
        console.error('Report charts could not be prepared', error);
        return { charts: [], warnings: [reportLabels.charts_unavailable] };
      });
      if (controller.signal.aborted) throw new DOMException('Report export cancelled', 'AbortError');
      abortRef.current = null;
      setProgress({ phase: 'sending', done: 0, total: 0 });
      const report = await api.createReportAPI(projectId, { charts: prepared.charts });
      return { report, warnings: [...prepared.warnings, ...(report.warnings ?? [])] };
    },
    onSuccess: async ({ report, warnings }) => {
      notifyReportCreated(report, warnings);
      queryClient.invalidateQueries({ queryKey: reportsKey(projectId) });
      try {
        const file = await api.downloadReportAPI(projectId, report.id);
        triggerFileDownload(file.blob, file.filename ?? report.filename);
      } catch {
        toast.error(reportLabels.error_created_not_downloaded);
      }
    },
    onError: async (error: unknown) => {
      if (isAbortError(error)) return;
      // A conflict means the cached context claimed results that are no longer up to date.
      queryClient.invalidateQueries({ queryKey: reportContextKey(projectId) });
      await notifyReportError(error);
    },
    onSettled: () => {
      abortRef.current = null;
      setProgress(null);
    },
  });

  return { ...mutation, progress, cancel, canCancel: mutation.isPending && progress?.phase !== 'sending' };
};

export const useDownloadReport = (projectId: string) => {
  const { downloadReportAPI } = Server();

  return useMutation({
    mutationFn: async (report: Report) => {
      const file = await downloadReportAPI(projectId, report.id);
      triggerFileDownload(file.blob, file.filename ?? report.filename);
    },
    onError: (error: unknown) => notifyReportError(error, reportLabels.error_download_failed),
  });
};
